import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
// Fix WH16 (2026-08-31): downloadBlob + Download para poder ABRIR los
// comprobantes (antes eran una fila muerta con un icono).
import api, { downloadBlob } from "@/lib/api";
import {
  ArrowLeft, CheckCircle, XCircle, Clock, Upload, Trash2,
  RefreshCw, DollarSign, FileText, Image, Download,
} from "lucide-react";

// Fix WH28 (2026-09-04): mismas cuatro categorias que captura OpsViaticoNuevo.
// Se muestran con el nombre legible; si algun dia aparece una categoria nueva,
// el render cae en la clave cruda y no rompe.
const BUDGET_LABELS = {
  comida: "Comida",
  transporte: "Transporte",
  hospedaje: "Hospedaje",
  otros: "Otros gastos",
};

const STATUS_CONFIG = {
  solicitada: { label: "Solicitada", color: "bg-slate-100 text-slate-700", Icon: Clock },
  aprobada: { label: "Aprobada", color: "bg-green-100 text-green-700", Icon: CheckCircle },
  rechazada: { label: "Rechazada", color: "bg-red-100 text-red-700", Icon: XCircle },
  adelanto_emitido: { label: "Adelanto emitido", color: "bg-blue-100 text-blue-700", Icon: DollarSign },
  en_viaje: { label: "En viaje", color: "bg-purple-100 text-purple-700", Icon: Clock },
  rendicion_pendiente: { label: "Rendicion pendiente", color: "bg-yellow-100 text-yellow-700", Icon: Clock },
  rendida: { label: "Rendida", color: "bg-teal-100 text-teal-700", Icon: CheckCircle },
  cerrada: { label: "Cerrada", color: "bg-slate-100 text-slate-500", Icon: CheckCircle },
  cancelada: { label: "Cancelada", color: "bg-red-100 text-red-500", Icon: XCircle },
};

const STEPS = [
  { key: "solicitada", label: "Solicitada" },
  { key: "aprobada", label: "Aprobada" },
  { key: "adelanto_emitido", label: "Adelanto" },
  { key: "en_viaje", label: "En viaje" },
  { key: "rendicion_pendiente", label: "Rendicion" },
  { key: "cerrada", label: "Cerrada" },
];

function Timeline({ status }) {
  const activeIdx = STEPS.findIndex(s => s.key === status);
  return (
    <div className="flex items-center">
      {STEPS.map((step, i) => (
        <div key={step.key} className="flex items-center">
          <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-colors ${
            i < activeIdx ? "bg-green-500 text-white" :
            i === activeIdx ? "bg-[#FFDF00] text-slate-900" :
            "bg-slate-200 text-slate-400"
          }`}>{i < activeIdx ? "✓" : i + 1}</div>
          <div className="ml-1 mr-3">
            <div className={`text-xs font-medium ${i === activeIdx ? "text-slate-900" : "text-slate-400"}`}>{step.label}</div>
          </div>
          {i < STEPS.length - 1 && <div className={`h-0.5 w-6 mr-3 ${i < activeIdx ? "bg-green-400" : "bg-slate-200"}`}></div>}
        </div>
      ))}
    </div>
  );
}

export default function OpsViaticoDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [viatico, setViatico] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showAdvance, setShowAdvance] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [approveNotes, setApproveNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [advance, setAdvance] = useState({ amount: "", method: "transferencia" });
  // Fix WH16 (2026-08-31): comprobante de la transferencia del adelanto.
  const [advanceFile, setAdvanceFile] = useState(null);
  const [receipt, setReceipt] = useState({ category: "comida", date: "", amount: "", vendor: "", notes: "" });
  const [receiptFile, setReceiptFile] = useState(null);

  // Fix WH11 (2026-08-30): un 403 (sin permiso) y un 404 (borrado) mostraban el
  // mismo "no encontrado" — la notificación llevaba acá y nadie entendía por qué
  // (video de Claudia 25/08, min 0:24). Ahora se distingue el motivo real.
  const [loadError, setLoadError] = useState(null);
  const fetch = useCallback(async () => {
    try {
      const res = await api.get(`/ops/viaticos/${id}`);
      setViatico(res.data);
      setLoadError(null);
    } catch (e) {
      console.error(e);
      setLoadError(e?.response?.status || "error");
    }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  const canApprove = ["corporate_admin", "treasury"].includes(user?.role);
  const isRequester = viatico?.requester_user_id === user?.id || canApprove;

  const action = async (fn) => {
    setActionLoading(true);
    try { await fn(); await fetch(); }
    catch (e) { alert(e?.response?.data?.detail || "Error al procesar la accion."); }
    finally { setActionLoading(false); }
  };

  const handleApprove = () => action(async () => {
    await api.post(`/ops/viaticos/${id}/approve`, { notes: approveNotes });
    setShowApprove(false);
  });

  const handleReject = () => action(async () => {
    await api.post(`/ops/viaticos/${id}/reject`, { reason: rejectReason });
    setShowReject(false);
  });

  const handleAdvance = () => action(async () => {
    await api.post(`/ops/viaticos/${id}/advance`, { amount: parseFloat(advance.amount), method: advance.method });
    // Fix WH16 (2026-08-31): si Tesoreria adjunto el comprobante de la
    // transferencia, se sube en un segundo request. Va DESPUES del adelanto
    // a proposito: el backend exige que el adelanto ya exista.
    if (advanceFile) {
      const fd = new FormData();
      fd.append("file", advanceFile);
      fd.append("notes", "");
      await api.post(`/ops/viaticos/${id}/advance-receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    }
    setAdvanceFile(null);
    setShowAdvance(false);
  });

  // Fix WH16 (2026-08-31): abre un archivo autenticado en otra pestana; si el
  // navegador bloquea el popup, lo descarga. Mismo helper que vigencias (WH11).
  const openAuthedFile = async (url, fallbackName) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const objUrl = URL.createObjectURL(res.data);
      const win = window.open(objUrl, "_blank");
      if (!win) downloadBlob(res.data, fallbackName);
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    } catch (e) {
      alert(e?.response?.data?.detail || "No se pudo abrir el archivo.");
    }
  };

  const handleOpenReceipt = (r) => openAuthedFile(`/ops/viaticos/${id}/receipts/${r.id}`, `comprobante-${r.vendor || "gasto"}`);
  const handleOpenAdvanceReceipt = () => openAuthedFile(`/ops/viaticos/${id}/advance-receipt`, viatico?.advance_receipt?.filename || "comprobante-adelanto");

  const handleReceiptUpload = () => action(async () => {
    if (!receiptFile) throw new Error("Selecciona una foto del comprobante.");
    const fd = new FormData();
    fd.append("file", receiptFile);
    fd.append("category", receipt.category);
    fd.append("amount", receipt.amount);
    fd.append("vendor", receipt.vendor);
    fd.append("receipt_date", receipt.date);
    fd.append("notes", receipt.notes);
    await api.post(`/ops/viaticos/${id}/receipts`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setShowReceipt(false);
    setReceiptFile(null);
    setReceipt({ category: "comida", date: "", amount: "", vendor: "", notes: "" });
  });

  const handleDeleteReceipt = (rid) => action(async () => {
    if (!confirm("Eliminar este comprobante?")) return;
    await api.delete(`/ops/viaticos/${id}/receipts/${rid}`);
  });

  const handleSubmitRendition = () => action(async () => {
    if (!confirm("Enviar la rendicion? Ya no podras agregar ni eliminar comprobantes.")) return;
    await api.post(`/ops/viaticos/${id}/submit-rendition`);
  });

  const handleApproveRendition = () => action(async () => {
    await api.post(`/ops/viaticos/${id}/approve-rendition`);
  });

  const handleCancel = () => action(async () => {
    await api.patch(`/ops/viaticos/${id}`, { status: "cancelada", cancel_reason: cancelReason });
    setShowCancel(false);
  });

  const handleDelete = () => action(async () => {
    if (!confirm("¿Borrar permanentemente esta solicitud? Esta accion no se puede deshacer.")) return;
    await api.delete(`/ops/viaticos/${id}`);
    navigate("/ops/viaticos");
  });

  if (loading) return <div className="p-6 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!viatico) {
    const msg = loadError === 404
      ? "Esta solicitud de viáticos ya no existe: probablemente fue borrada después de que se generó la notificación."
      : loadError === 403
        ? "No tienes acceso a esta solicitud de viáticos. Pide acceso al administrador si crees que deberías verla."
        : "No se pudo cargar la solicitud de viáticos. Intenta de nuevo en unos segundos.";
    return <div className="p-6 text-slate-500">{msg}</div>;
  }

  const { status } = viatico;
  const cfg = STATUS_CONFIG[status] || { label: status, color: "bg-slate-100 text-slate-600", Icon: Clock };
  const Icon = cfg.Icon;
  const canCancel = ["solicitada", "aprobada"].includes(status) && (isRequester || canApprove);
  const canDelete = ["solicitada", "cancelada"].includes(status) && (user?.role === "corporate_admin" || viatico.requester_user_id === user?.id);

  const totalBudget = Object.values(viatico.budget_breakdown || {}).reduce((s, v) => s + (v?.total || 0), 0);

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="viatico-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/ops/viaticos")} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{viatico.request_number}</h1>
            <p className="text-sm text-slate-500">{viatico.destination_description}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${cfg.color}`}>
          <Icon className="w-4 h-4" /> {cfg.label}
        </span>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 overflow-x-auto">
        <Timeline status={status} />
      </div>

      {/* Info */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 grid grid-cols-2 gap-4 text-sm">
        {/* Fix WH28 (2026-09-04): quien aprueba veia destino, fechas y monto,
            pero no de quien era la solicitud. Va primero porque es lo primero
            que se pregunta al abrir una aprobacion. */}
        <div data-testid="viatico-solicitante">
          <span className="text-slate-500">Solicita:</span>{" "}
          <span className="font-medium">{viatico.requester_name || viatico.requester_email || "—"}</span>
        </div>
        <div><span className="text-slate-500">Sucursal de origen:</span> <span className="font-medium">{viatico.branch_origin_name || "—"}</span></div>
        <div><span className="text-slate-500">Destino:</span> <span className="font-medium">{viatico.destination_description}</span></div>
        <div><span className="text-slate-500">Proposito:</span> <span className="font-medium">{viatico.purpose}</span></div>
        <div><span className="text-slate-500">Salida:</span> <span className="font-medium">{viatico.trip_start}</span></div>
        <div><span className="text-slate-500">Regreso:</span> <span className="font-medium">{viatico.trip_end}</span></div>
        <div><span className="text-slate-500">Solicitado:</span> <span className="font-bold">${(viatico.total_requested || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
        {viatico.advance_amount && (
          <div>
            <span className="text-slate-500">Adelanto:</span>{" "}
            <span className="font-bold text-blue-600">${(viatico.advance_amount || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} ({viatico.advance_method})</span>
            {/* Fix WH16 (2026-08-31): comprobante de la transferencia del adelanto. */}
            {viatico.advance_receipt?.storage_path ? (
              <button onClick={handleOpenAdvanceReceipt} data-testid="btn-abrir-comprobante-adelanto"
                className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline align-middle">
                <Download className="w-3.5 h-3.5" /> Ver comprobante
              </button>
            ) : (
              <span className="ml-2 text-xs text-amber-600" data-testid="adelanto-sin-comprobante">sin comprobante</span>
            )}
          </div>
        )}
        {viatico.total_spent > 0 && <div><span className="text-slate-500">Gastado:</span> <span className="font-bold">${(viatico.total_spent || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>}
        {viatico.balance !== undefined && viatico.advance_amount && (
          <div>
            <span className="text-slate-500">Balance:</span>{" "}
            <span className={`font-bold ${viatico.balance >= 0 ? "text-green-600" : "text-red-600"}`}>
              {viatico.balance >= 0 ? "Devuelve" : "A reembolsar"} ${Math.abs(viatico.balance || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
            </span>
          </div>
        )}
        {viatico.approval_notes && <div className="col-span-2"><span className="text-slate-500">Nota aprobacion:</span> <span>{viatico.approval_notes}</span></div>}
        {viatico.rejection_reason && <div className="col-span-2 text-red-600"><span className="text-slate-500">Motivo rechazo:</span> <span>{viatico.rejection_reason}</span></div>}
        {viatico.cancel_reason && <div className="col-span-2 text-orange-600"><span className="text-slate-500">Motivo cancelacion:</span> <span>{viatico.cancel_reason}</span></div>}
      </div>

      {/* Desglose de lo solicitado */}
      {/* Fix WH28 (2026-09-04): el desglose se captura en la solicitud y se
          guarda completo en `budget_breakdown` (comida con monto por dia y
          cantidad de dias, transporte, hospedaje, otros), pero no se mostraba
          en ninguna pantalla: quien aprobaba veia un total y nada mas.
          `totalBudget` ya se calculaba unas lineas arriba y no se usaba en
          ningun lado — esta tarjeta nunca se llego a construir. */}
      {Object.keys(viatico.budget_breakdown || {}).length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="viatico-desglose">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Desglose solicitado</h3>
          <div className="space-y-2 text-sm">
            {Object.entries(viatico.budget_breakdown).map(([key, val]) => {
              const total = Number(val?.total || 0);
              if (!total) return null;
              return (
                <div key={key} data-testid={`desglose-${key}`}
                  className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                  <div>
                    <span className="font-medium text-slate-800">{BUDGET_LABELS[key] || key}</span>
                    {Number(val?.per_day) > 0 && Number(val?.days) > 0 && (
                      <span className="text-xs text-slate-500 ml-2">
                        ${Number(val.per_day).toLocaleString("es-MX")} x {val.days} dias
                      </span>
                    )}
                  </div>
                  <span className="font-semibold">${total.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
                </div>
              );
            })}
            <div className="flex items-center justify-between pt-2 text-slate-900">
              <span className="font-semibold">Total</span>
              <span className="font-bold" data-testid="desglose-total">
                ${totalBudget.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        {status === "solicitada" && canApprove && (
          <>
            <button onClick={() => setShowApprove(true)} data-testid="btn-aprobar"
              className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors">
              Aprobar
            </button>
            <button onClick={() => setShowReject(true)} data-testid="btn-rechazar"
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors">
              Rechazar
            </button>
          </>
        )}
        {status === "aprobada" && canApprove && (
          <button onClick={() => setShowAdvance(true)} data-testid="btn-registrar-adelanto"
            className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors">
            Registrar adelanto
          </button>
        )}
        {["adelanto_emitido", "en_viaje"].includes(status) && isRequester && (
          <>
            <button onClick={() => setShowReceipt(true)} data-testid="btn-subir-comprobante"
              className="px-4 py-2 bg-[#FFDF00] text-slate-900 rounded-lg text-sm font-semibold hover:bg-yellow-400 transition-colors">
              Subir comprobante
            </button>
            {viatico.receipts?.length > 0 && (
              <button onClick={handleSubmitRendition} disabled={actionLoading} data-testid="btn-rendir"
                className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors disabled:opacity-50">
                Enviar rendicion
              </button>
            )}
          </>
        )}
        {status === "rendicion_pendiente" && canApprove && (
          <button onClick={handleApproveRendition} disabled={actionLoading} data-testid="btn-aprobar-rendicion"
            className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors disabled:opacity-50">
            Aprobar rendicion y cerrar
          </button>
        )}
        {canCancel && (
          <button onClick={() => setShowCancel(true)} data-testid="btn-cancelar-viatico"
            className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm font-medium hover:bg-orange-200 transition-colors">
            Cancelar solicitud
          </button>
        )}
        {canDelete && (
          <button onClick={handleDelete} disabled={actionLoading} data-testid="btn-borrar-viatico"
            className="px-4 py-2 bg-slate-100 text-slate-500 rounded-lg text-sm font-medium hover:bg-red-100 hover:text-red-600 transition-colors disabled:opacity-50">
            Borrar
          </button>
        )}
      </div>

      {/* Receipts */}
      {(viatico.receipts?.length > 0 || ["adelanto_emitido","en_viaje"].includes(status)) && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Comprobantes ({viatico.receipts?.length || 0})</h3>
            <div className="text-sm font-medium">Total: <span className="text-slate-900 font-bold">${(viatico.total_spent || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
          </div>
          {viatico.receipts?.length === 0 ? (
            <p className="text-sm text-slate-400">Sin comprobantes todavia.</p>
          ) : (
            <div className="space-y-2">
              {/* Fix WH16 (2026-08-31): la fila era estatica - se veia el icono
                  pero el ticket no se abria, asi que contabilidad no podia
                  validar nada (video de Claudia 25/08, min 23:02). */}
              {viatico.receipts.map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3 cursor-pointer min-w-0 flex-1" onClick={() => handleOpenReceipt(r)} data-testid={`comprobante-abrir-${r.id}`}>
                    <div className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Image className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium hover:underline truncate">{r.vendor} — <span className="capitalize">{r.category}</span></div>
                      <div className="text-xs text-slate-500">{r.date}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">${(r.amount || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
                    <button onClick={() => handleOpenReceipt(r)} title="Abrir / descargar" data-testid={`btn-abrir-comprobante-${r.id}`}
                      className="p-1.5 text-slate-400 hover:text-slate-900 transition-colors">
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    {!viatico.rendition_submitted_at && isRequester && (
                      <button onClick={() => handleDeleteReceipt(r.id)} className="p-1.5 text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Approve modal */}
      {showApprove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowApprove(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-slate-900">Aprobar viatico</h3>
            <textarea value={approveNotes} onChange={e => setApproveNotes(e.target.value)} rows={3}
              placeholder="Notas (opcional)" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none" />
            <div className="flex gap-3">
              <button onClick={() => setShowApprove(false)} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={handleApprove} disabled={actionLoading}
                className="flex-1 py-2 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600 disabled:opacity-50">
                {actionLoading ? "..." : "Aprobar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {showReject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowReject(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-slate-900">Rechazar viatico</h3>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} required
              placeholder="Motivo del rechazo (obligatorio, min 5 caracteres)" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none" />
            <div className="flex gap-3">
              <button onClick={() => setShowReject(false)} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={handleReject} disabled={actionLoading || rejectReason.length < 5}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 disabled:opacity-50">
                {actionLoading ? "..." : "Rechazar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advance modal */}
      {showAdvance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAdvance(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-slate-900">Registrar adelanto</h3>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Monto ($)</label>
              <input type="number" value={advance.amount} onChange={e => setAdvance(a => ({ ...a, amount: e.target.value }))}
                placeholder={`Max: $${(viatico.total_requested || 0).toLocaleString("es-MX")}`}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Metodo</label>
              <select value={advance.method} onChange={e => setAdvance(a => ({ ...a, method: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                {[["transferencia","Transferencia"],["efectivo","Efectivo"],["viaticos_card","Tarjeta viaticos"]].map(([v,l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            {/* Fix WH16 (2026-08-31): comprobante de la transferencia (opcional
                al registrar, se puede subir despues volviendo a abrir el modal). */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Comprobante de la transferencia</label>
              <input type="file" accept="image/*,application/pdf" data-testid="input-comprobante-adelanto"
                onChange={e => setAdvanceFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700" />
              <p className="text-xs text-slate-400 mt-1">JPG, PNG o PDF, hasta 8 MB. Opcional.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setAdvanceFile(null); setShowAdvance(false); }} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={handleAdvance} disabled={!advance.amount || actionLoading}
                className="flex-1 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 disabled:opacity-50">
                {actionLoading ? "..." : "Registrar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel modal */}
      {showCancel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCancel(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-slate-900">Cancelar solicitud</h3>
            <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={3} required
              placeholder="Motivo de la cancelacion (obligatorio, min 5 caracteres)"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none"
              data-testid="viatico-cancel-reason-input" />
            <div className="flex gap-3">
              <button onClick={() => setShowCancel(false)} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50">Volver</button>
              <button onClick={handleCancel} disabled={actionLoading || cancelReason.length < 5} data-testid="btn-confirmar-cancelacion"
                className="flex-1 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 disabled:opacity-50">
                {actionLoading ? "..." : "Confirmar cancelacion"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt modal */}
      {showReceipt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowReceipt(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-slate-900">Subir comprobante</h3>
            {[
              { label: "Categoria", field: "category", type: "select", options: [["comida","Comida"],["transporte","Transporte"],["hospedaje","Hospedaje"],["otros","Otros"]] },
              { label: "Fecha", field: "date", type: "date" },
              { label: "Monto ($)", field: "amount", type: "number", placeholder: "0.00" },
              { label: "Proveedor / establecimiento", field: "vendor", type: "text", placeholder: "Ej: Restaurant XYZ" },
              { label: "Notas (opcional)", field: "notes", type: "text", placeholder: "" },
            ].map(({ label, field, type, placeholder, options }) => (
              <div key={field}>
                <label className="text-sm font-medium text-slate-700 mb-1 block">{label}</label>
                {type === "select" ? (
                  <select value={receipt[field]} onChange={e => setReceipt(r => ({ ...r, [field]: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                ) : (
                  <input type={type} value={receipt[field]} placeholder={placeholder}
                    onChange={e => setReceipt(r => ({ ...r, [field]: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
                )}
              </div>
            ))}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Foto del comprobante *</label>
              <label className={`flex items-center gap-2 p-3 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${receiptFile ? "border-green-300 bg-green-50" : "border-slate-200 hover:border-yellow-300"}`}>
                <Upload className="w-4 h-4 text-slate-400" />
                <span className="text-sm">{receiptFile ? receiptFile.name : "Subir foto o PDF"}</span>
                <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,.heic" capture="environment" className="hidden"
                  onChange={e => setReceiptFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowReceipt(false)} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={handleReceiptUpload} disabled={!receiptFile || !receipt.amount || actionLoading}
                className="flex-1 py-2 bg-[#FFDF00] text-slate-900 rounded-lg text-sm font-semibold hover:bg-yellow-400 disabled:opacity-50">
                {actionLoading ? "..." : "Subir"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
